import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useActiveRealmAPIContext } from '@interxfi/api-client';
import { useIxcMutualfundsV1FundOrderQueryServiceFundOrderGet } from '@interxfi/api-client/generated/hooks';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import { Typography } from '@interxfi/design-system/components/typography';
import { MutualFundsFundOrderActionPill } from '../../../../../../../../../../../features/mutual-funds/fund-order/mutual-funds-fund-order-action-pill';
import { MutualFundsFundOrderEntityMenu } from '../../../../../../../../../../../features/mutual-funds/fund-order/mutual-funds-fund-order-entity-menu';
import { MutualFundsFundOrderEventSummary } from '../../../../../../../../../../../features/mutual-funds/fund-order/mutual-funds-fund-order-event';
import { getMutualFundsFundOrderDetailPageListItems } from '../../../../../../../../../../../features/mutual-funds/fund-order/mutual-funds-fund-order-list-items';
import { getMutualFundsFundOrderDetailPath } from '../../../../../../../../../../../features/mutual-funds/fund-order/mutual-funds-fund-order-routing';
import { MutualFundsFundOrderStatusPill } from '../../../../../../../../../../../features/mutual-funds/fund-order/mutual-funds-fund-order-status-pill';
import { MutualFundsFundOrderSummaryBar } from '../../../../../../../../../../../features/mutual-funds/fund-order/mutual-funds-fund-order-summary-bar';
import { OrderInstructionTaxLotSelectionDetail } from '../../../../../../../../../../../features/order/order-instruction-tax-lot-selection-detail';
import { RegistrationAccountPageHeader } from '../../../../../../../../../../../features/registration/account/registration-account-page-header';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute(
  '/_app/_clearing-core/registration/account/$accountId/_account/_detail/order/mutual-fund/_mutual-fund/$fundOrderId',
)({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `account-mutual-fund-order-${params.fundOrderId}`,
        label: 'Order',
        to: getMutualFundsFundOrderDetailPath(params.fundOrderId, params.accountId),
        suffix: (
          <CopyButton
            content={params.fundOrderId}
            label="Copy Fund Order ID"
            tooltip={{ copy: params.fundOrderId, copied: 'Copied Fund Order ID' }}
          />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Order'),
  component: AccountOrderDetail,
});

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load mutual fund order', 'view this mutual fund order');

function AccountOrderDetail() {
  const { accountId, fundOrderId } = Route.useParams();
  const request = { fundOrderId } as const;
  const { data, error, isPending } = useIxcMutualfundsV1FundOrderQueryServiceFundOrderGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const { activeTenantIsClearing } = useActiveRealmAPIContext();
  const listItems = useMemo(
    () =>
      getMutualFundsFundOrderDetailPageListItems(data?.fundOrder, {
        fundOrderId: false,
        accountId: false,
        programId: true,
        venueOrderId: activeTenantIsClearing,
      }),
    [data?.fundOrder, activeTenantIsClearing],
  );

  return (
    <PageLayout gap={7} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <RegistrationAccountPageHeader
          accountId={accountId}
          actions={<MutualFundsFundOrderEntityMenu fundOrderId={fundOrderId} />}
          pills={
            <>
              <MutualFundsFundOrderStatusPill isPending={showPending} size="large" status={data?.fundOrder?.status} />
              <MutualFundsFundOrderActionPill action={data?.fundOrder?.data?.instruction?.action} isPending={showPending} size="large" />
            </>
          }
        />
      </PageLayout.Section>

      <PageLayout.Section noBorder>
        <MutualFundsFundOrderSummaryBar fundOrderId={fundOrderId} />
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <MutualFundsFundOrderEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: (
                <VerticalStack gap={4}>
                  <AppReadonlyListStack groups={listItems.left} isPending={showPending} />

                  {data?.fundOrder?.data?.instruction?.taxLotSelection ? (
                    <OrderInstructionTaxLotSelectionDetail
                      accountId={accountId}
                      assetId={data?.fundOrder?.data?.asset?.assetId}
                      taxLotSelection={data.fundOrder.data.instruction.taxLotSelection}
                    />
                  ) : null}
                </VerticalStack>
              ),
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
